import { Fragment as _Fragment, jsxs as _jsxs, jsx as _jsx } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { css, useTheme } from 'styled-components';
import { Box } from '@design-system/box';
import { useI18n } from '@design-system/i18n';
import { Loader } from '@design-system/loader';
import { DefaultEmptyState } from './DefaultEmptyState';
import { useInfiniteItems } from './hooks/useInfiniteItems';
import { useScreenReaderStatus } from './hooks/useScreenReaderStatus';
import { useTagsAndSort } from './hooks/useTagsAndSort';
import { InfiniteLoadObserver } from './InfiniteLoadOberver';
import { ItemsContent } from './ItemsContent';
import { LoadMoreButton } from './LoadMoreButton';
import { TitleBlock } from './TitleBlock';
import { getEffectiveSortBy, getFetchFilters, getFetchNextPage, getHasNoResult, getItemsSize, } from './utils';
import { TagsFilters } from '../Tags/TagsFilters';
const DefaultTitle = ({ isLoading, itemsSize }) => {
    const { __ } = useI18n();
    return (_jsxs(_Fragment, { children: [__('Results'), " ", !isLoading && `(${itemsSize})`] }));
};
const defaultGetDataTags = ({ data }) => data === null || data === void 0 ? void 0 : data['block-tags'];
const defaultGetDataFilters = ({ data }) => data === null || data === void 0 ? void 0 : data['block-filters'];
export const BlockItems = forwardRef(({ blockId, blockQuery, clearFilters, columnsInMobile, data, dataTestId, emptyState = DefaultEmptyState, fetchItems, getDataFilters = defaultGetDataFilters, getDataTags = defaultGetDataTags, hideTitle = false, infiniteLoad = true, initialFetchFilters, initialPage = 1, initialSort, itemLoader, itemRenderer, itemsByRow = 4, itemsContentRenderer: ItemsContentRenderer = ItemsContent, loaderLines = 3, noResultsMessage, pb, resultsFoundMessage, rowGap = 6, setBlockData, setBlockQuery, setScreenReaderStatus, shouldHideIfEmpty = false, sortBy, tags, title = DefaultTitle, titleAs = 'h2', titleRight, topicId, width, withCursorPagination = false, }, ref) => {
    var _a;
    const theme = useTheme();
    const { handleChangeSort, handleChangeTags, selectedSort, selectedTags } = useTagsAndSort({
        blockId,
        blockQuery,
        setBlockQuery,
        sortBy,
    });
    const resetTags = () => {
        handleChangeTags([]);
    };
    const dataFilters = getDataFilters === null || getDataFilters === void 0 ? void 0 : getDataFilters({ data });
    const dataTags = getDataTags === null || getDataTags === void 0 ? void 0 : getDataTags({ data });
    const managesTagsInternally = Boolean(tags);
    const fetchFilters = getFetchFilters({
        dataFilters,
        initialFetchFilters,
        managesTagsInternally,
        selectedTags,
        dataTags,
        selectedSort,
        initialSort,
    });
    const { data: fetchedData, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, isLoading, } = useInfiniteItems({
        blockId,
        fetchFilters,
        fetchItems,
        initialPage,
        topicId,
        withCursorPagination,
    });
    const itemsSize = getItemsSize({ fetchedData });
    const hasNoResult = getHasNoResult({ isLoading, itemsSize });
    const status = useScreenReaderStatus({
        isFetching,
        isLoading,
        itemsSize,
        hasNoResult,
        resultsFoundMessage,
        noResultsMessage,
    });
    useEffect(() => {
        if (status !== null) {
            setScreenReaderStatus(status);
        }
    }, [setScreenReaderStatus, status]);
    const handleClearFilters = () => clearFilters();
    useEffect(() => {
        const items = fetchedData === null || fetchedData === void 0 ? void 0 : fetchedData.pages.filter(Boolean).filter((page) => 'items' in page && Array.isArray(page.items)).flatMap((page) => page.items);
        setBlockData(items || []);
    }, [fetchedData, setBlockData]);
    useImperativeHandle(ref, () => ({
        fetchNextPage: getFetchNextPage({ hasNextPage, fetchNextPage }),
        resetTags,
        checkHasTags: () => Boolean(selectedTags === null || selectedTags === void 0 ? void 0 : selectedTags.length),
    }));
    if (shouldHideIfEmpty &&
        hasNoResult &&
        !fetchFilters.filters &&
        !((_a = fetchFilters === null || fetchFilters === void 0 ? void 0 : fetchFilters.tags) === null || _a === void 0 ? void 0 : _a.length)) {
        return null;
    }
    const effectiveSortBy = getEffectiveSortBy({
        isLoading,
        itemsSize,
        sortBy,
    });
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { css: css `
            > * + * {
              margin-top: ${theme.space[4]}px;
            }
          `, "data-testid": dataTestId, display: "block", id: blockId, children: [(tags === null || tags === void 0 ? void 0 : tags.position) === 'before-title' && (_jsx(TagsFilters, Object.assign({}, tags, { onClick: handleChangeTags, selectedTags: selectedTags }))), !hideTitle && (_jsx(TitleBlock, { blockId: blockId, isLoading: isLoading, itemsSize: itemsSize, onChange: handleChangeSort, sortBy: effectiveSortBy, title: title, titleAs: titleAs, titleRight: titleRight, value: fetchFilters.sort, width: width })), (tags === null || tags === void 0 ? void 0 : tags.position) === 'after-title' && (_jsx(TagsFilters, Object.assign({}, tags, { onClick: handleChangeTags, selectedTags: selectedTags })))] }), _jsx(ItemsContentRenderer, { blockId: blockId, clearFilters: handleClearFilters, columnsInMobile: columnsInMobile, emptyState: emptyState, fetchedData: fetchedData, hasNoResult: hasNoResult, isLoading: isLoading, itemLoader: itemLoader, itemRenderer: itemRenderer, itemsByRow: itemsByRow, loaderLines: loaderLines, noResultsMessage: noResultsMessage, pb: pb, rowGap: rowGap, title: title }), infiniteLoad && isFetchingNextPage && hasNextPage && (_jsx(Box, { "data-testid": "load-more", pb: 6, pt: 6, children: _jsx(Loader, { size: 30 }) })), !infiniteLoad && !isLoading && hasNextPage && (_jsx(LoadMoreButton, { handleLoadNewPage: fetchNextPage, isLoading: isFetchingNextPage })), infiniteLoad && hasNextPage && !isFetchingNextPage && !isLoading && (_jsx(InfiniteLoadObserver, { onIntersect: fetchNextPage, thresholdAbove: 4000 }))] }));
});
//# sourceMappingURL=index.js.map